import React, { useEffect, useState } from "react";
import BrandLogo from "../components/common/BrandLogo";
import { Helmet } from "react-helmet-async";
import {
  MapPin, Ruler, Shield, TrendingUp, CheckCircle2, ChevronDown, Phone, MessageCircle, ArrowRight, Compass, ScrollText,
} from "lucide-react";
import { useSiteSettings } from "../lib/settings";
import { resolveImage } from "../components/admin/ImageUpload";
import BitrixFormEmbed from "../components/common/BitrixFormEmbed";
import Footer from "../components/layout/Footer";

const DEFAULT_HERO = "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=2200&q=80";

/* Pick A/B variant — stable per user session, honors ?variant=A|B override. */
const pickVariant = (abEnabled) => {
  if (!abEnabled || typeof window === "undefined") return "A";
  const params = new URLSearchParams(window.location.search);
  const forced = (params.get("variant") || "").toUpperCase();
  if (forced === "A" || forced === "B") {
    try { window.sessionStorage.setItem("re_variant", forced); } catch { /* ignore */ }
    return forced;
  }
  try {
    const cached = window.sessionStorage.getItem("re_variant");
    if (cached === "A" || cached === "B") return cached;
    const v = Math.random() < 0.5 ? "A" : "B";
    window.sessionStorage.setItem("re_variant", v);
    return v;
  } catch {
    return Math.random() < 0.5 ? "A" : "B";
  }
};

const buildVariantQuery = () => {
  if (typeof window === "undefined") return "";
  const p = new URLSearchParams(window.location.search);
  const parts = [];
  ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"].forEach((k) => {
    const v = p.get(k);
    if (v) parts.push(`${k}=${encodeURIComponent(v)}`);
  });
  return parts.join("&");
};

/* ------------------------------------------------------------------ */
/* FAQ Accordion                                                        */
/* ------------------------------------------------------------------ */
const Faq = ({ items }) => {
  const [open, setOpen] = useState(-1);
  if (!items?.length) return null;
  return (
    <div className="divide-y divide-white/10 border border-white/10">
      {items.map((it, i) => (
        <div key={i} className="bg-brand-navy/40">
          <button
            type="button"
            onClick={() => setOpen(open === i ? -1 : i)}
            className="w-full flex items-center justify-between text-left px-6 py-5 text-white hover:bg-white/5 transition-colors"
            data-testid={`re-faq-${i}`}
          >
            <span className="font-medium pr-4">{it.q}</span>
            <ChevronDown size={18} className={`shrink-0 transition-transform ${open === i ? "rotate-180" : ""}`} />
          </button>
          {open === i ? (
            <div className="px-6 pb-6 text-white/80 leading-relaxed text-sm">{it.a}</div>
          ) : null}
        </div>
      ))}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Page                                                                 */
/* ------------------------------------------------------------------ */
const RealEstateLandingPage = () => {
  const s = useSiteSettings();
  const [variant, setVariant] = useState("A");

  useEffect(() => {
    if (s) setVariant(pickVariant(s.real_estate_ab_enabled === true));
  }, [s]);

  // Gate: admin can disable this landing page from Site Settings
  if (s && s.real_estate_enabled === false) {
    return (
      <div className="min-h-screen bg-brand-navy text-white flex flex-col items-center justify-center px-6 text-center" data-testid="realestate-disabled">
        <div className="text-[0.66rem] tracking-[0.32em] uppercase text-brand-gold">Şu an kullanılamıyor</div>
        <h1 className="font-serif text-3xl md:text-4xl mt-4 max-w-xl">Bu sayfa geçici olarak devre dışı bırakıldı.</h1>
        <a href="/" className="mt-8 inline-flex items-center gap-2 bg-brand-gold hover:bg-brand-gold/90 text-brand-navy font-medium tracking-[0.22em] uppercase text-[0.72rem] px-8 py-4">Ana sayfaya dön</a>
      </div>
    );
  }

  const isB = variant === "B" && s.real_estate_ab_enabled === true;
  const heroKicker = isB && s.real_estate_hero_kicker_b ? s.real_estate_hero_kicker_b : (s.real_estate_hero_kicker || "YENİŞEHİR / ARNAVUTKÖY");
  const heroTitle = isB && s.real_estate_hero_title_b ? s.real_estate_hero_title_b : (s.real_estate_hero_title || "İstanbul'un yeni değerlenme merkezinde Arsa Yatırımı");
  const heroSubtitle = isB && s.real_estate_hero_subtitle_b ? s.real_estate_hero_subtitle_b : s.real_estate_hero_subtitle;
  const heroImageResolved = resolveImage(isB && s.real_estate_hero_image_b ? s.real_estate_hero_image_b : s.real_estate_hero_image);
  const heroImage = heroImageResolved || DEFAULT_HERO;
  const phone = s.real_estate_phone || s.contact_phone || "";
  const wa = (s.real_estate_whatsapp || s.whatsapp_number || "").replace(/\D/g, "");
  const bullets = s.real_estate_hero_bullets || [];
  const stats = s.real_estate_trust_stats || [];
  const advantages = s.real_estate_advantages || [];
  const opportunities = s.real_estate_opportunities || [];
  const faq = s.real_estate_faq || [];
  const embed = s.real_estate_map_embed || "";
  const bitrixForms = s.bitrix_forms || [];
  const bitrixExtra = buildVariantQuery();

  const scrollToForm = () => {
    if (typeof document !== "undefined") {
      const el = document.getElementById("re-lead-form");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <div className="bg-white text-brand-dark" data-testid="realestate-landing" data-variant={variant}>
      <Helmet>
        <title>Yenişehir / Arnavutköy Arsa Yatırımı — Fırat Emlak</title>
        <meta name="description" content="Yenişehir / Arnavutköy bölgesinde tapulu, yüksek getirili arsa fırsatları. 3. Havalimanı ve Kanal İstanbul aksında yatırım danışmanlığı." />
        <link rel="canonical" href={typeof window !== "undefined" ? `${window.location.origin}/realestate` : ""} />
        <meta property="og:title" content="Yenişehir / Arnavutköy Arsa Yatırımı" />
        <meta property="og:description" content="İstanbul'un yeni değerlenme merkezinde tapulu arsa fırsatları." />
        {heroImage ? <meta property="og:image" content={heroImage} /> : null}
      </Helmet>

      {/* Minimal top bar */}
      <header className="bg-brand-navy text-white">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 h-16 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3" aria-label="Fırat Construction anasayfa" data-testid="re-home-link">
            <BrandLogo place="header" onDark max={40} />
          </a>
          <div className="flex items-center gap-4 text-xs">
            {phone ? <a href={`tel:${phone}`} className="hidden sm:inline-flex items-center gap-2 hover:text-brand-gold" data-testid="re-top-call"><Phone size={14} /> {phone}</a> : null}
            {wa ? <a href={`https://wa.me/${wa}?text=${encodeURIComponent(s.whatsapp_message || "Yenişehir arsa yatırımı hakkında bilgi almak istiyorum.")}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-brand-gold hover:bg-brand-gold/90 text-brand-navy font-medium tracking-[0.22em] uppercase text-[0.7rem] px-4 py-2" data-testid="re-top-wa"><MessageCircle size={14} /> WhatsApp</a> : null}
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden" data-testid="re-hero">
        <img src={heroImage} alt="Yenişehir / Arnavutköy arsa yatırımı" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0B1631]/95 via-[#0B1631]/75 to-[#0B1631]/40" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, transparent 60%, rgba(11,22,49,0.9) 100%)" }} />

        <div className="relative z-10 mx-auto max-w-[1400px] px-6 md:px-10 py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 w-full">
          <div className="lg:col-span-7 text-white">
            <div className="text-[0.7rem] tracking-[0.4em] uppercase text-brand-gold">{heroKicker}</div>
            <h1 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight" data-testid="re-hero-title">
              {heroTitle}
            </h1>
            <p className="mt-6 text-lg text-white/85 max-w-2xl leading-relaxed">
              {heroSubtitle}
            </p>

            <ul className="mt-8 space-y-3">
              {bullets.map((b, i) => (
                <li key={i} className="flex items-center gap-3 text-white/90">
                  <CheckCircle2 size={18} className="text-brand-gold shrink-0" /> {b}
                </li>
              ))}
            </ul>

            <div className="mt-10 flex flex-wrap gap-3">
              <button onClick={() => scrollToForm()} className="inline-flex items-center gap-2 bg-brand-gold hover:bg-brand-gold/90 text-brand-navy font-medium tracking-[0.22em] uppercase text-[0.72rem] px-8 py-4 transition-colors" data-testid="re-hero-cta">
                Danış &amp; Bilgi Al <ArrowRight size={16} />
              </button>
              {phone ? (
                <a href={`tel:${phone}`} className="inline-flex items-center gap-2 border border-white/30 hover:border-brand-gold hover:text-brand-gold text-white tracking-[0.22em] uppercase text-[0.72rem] px-8 py-4 transition-colors" data-testid="re-hero-call">
                  <Phone size={14} /> Hemen Ara
                </a>
              ) : null}
            </div>
          </div>

          {/* Lead card (desktop) — Bitrix "Danış & Bilgi Al" form */}
          <div className="lg:col-span-5">
            <div className="bg-white/95 backdrop-blur border border-white/20 p-6 md:p-8" data-testid="re-hero-form-card">
              <div className="text-[0.66rem] tracking-[0.3em] uppercase text-brand-medium">Ücretsiz Yatırım Danışmanlığı</div>
              <div className="font-serif text-2xl text-brand-navy mt-2">Danış &amp; Bilgi Al</div>
              <p className="text-sm text-brand-medium mt-2 mb-5">Formu doldurun; uzmanımız saha dosyasını, güncel fiyat listesini ve tapu detaylarını sizinle paylaşsın.</p>
              <BitrixFormEmbed forms={bitrixForms} formKey="buy" extraQuery={bitrixExtra} testId="re-hero-bitrix" />
            </div>
          </div>
        </div>
      </section>

      {/* Trust stats */}
      {stats.length > 0 ? (
        <section className="bg-brand-navy text-white">
          <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-10 grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((st, i) => (
              <div key={i} className="text-center md:text-left">
                <div className="font-serif text-4xl text-brand-gold tabular-nums">{st.value}</div>
                <div className="mt-2 text-[0.66rem] tracking-[0.3em] uppercase text-white/70">{st.label}</div>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      {/* Advantages */}
      <section className="py-24 bg-brand-light">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <div className="max-w-3xl">
            <div className="text-[0.66rem] tracking-[0.3em] uppercase text-brand-gold">Neden Yenişehir / Arnavutköy?</div>
            <h2 className="mt-4 font-serif text-3xl md:text-4xl text-brand-navy">İstanbul&apos;un yeni yatırım koridoru</h2>
            <p className="mt-4 text-brand-medium">
              3. Havalimanı ile hayat bulan, Kanal İstanbul projesiyle stratejik önemi katlanan bölge; kısa vadede kentsel dönüşüm, uzun vadede lüks projelerin merkezi olmaya aday.
            </p>
          </div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="re-advantages">
            {advantages.map((a, i) => {
              const Icons = [TrendingUp, Shield, Compass, ScrollText];
              const Icon = Icons[i % Icons.length];
              return (
                <div key={i} className="bg-white p-8 border-l-2 border-brand-gold hover:shadow-lg transition-shadow">
                  <Icon size={28} strokeWidth={1.4} className="text-brand-navy" />
                  <div className="mt-6 font-serif text-xl text-brand-navy">{a.title}</div>
                  <p className="mt-3 text-sm text-brand-medium leading-relaxed">{a.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Opportunities */}
      <section className="py-24 bg-white">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <div className="text-[0.66rem] tracking-[0.3em] uppercase text-brand-gold">Güncel Fırsatlar</div>
              <h2 className="mt-4 font-serif text-3xl md:text-4xl text-brand-navy">Yatırıma hazır arsalar</h2>
            </div>
            <div className="text-sm text-brand-medium max-w-md">Her arsa; tapu, imar durumu, ekspertiz ve gelecek projeksiyonu ile birlikte sunulur.</div>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="re-opportunities">
            {opportunities.map((o, i) => {
              const img = resolveImage(o.image) || `https://images.unsplash.com/photo-1500382017468-9049fed747ef?auto=format&fit=crop&w=1400&q=80&sig=${i}`;
              return (
                <article key={i} className="group border border-black/10 bg-white flex flex-col overflow-hidden hover:shadow-xl transition-shadow" data-testid={`re-opp-${i}`}>
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img src={img} alt={o.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    {o.badge ? <span className="absolute top-4 left-4 bg-brand-gold text-brand-navy text-[0.62rem] tracking-[0.28em] uppercase px-3 py-1.5 font-medium">{o.badge}</span> : null}
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <div className="flex items-center gap-2 text-xs text-brand-medium">
                      <MapPin size={12} /> {o.location}
                    </div>
                    <div className="mt-3 font-serif text-lg text-brand-navy leading-snug">{o.title}</div>
                    <p className="mt-3 text-sm text-brand-medium leading-relaxed">{o.description}</p>

                    <div className="mt-5 flex flex-wrap gap-2">
                      {(o.features || []).map((f, k) => (
                        <span key={k} className="inline-flex items-center gap-1.5 text-[0.62rem] tracking-[0.2em] uppercase text-brand-navy bg-brand-light border border-black/10 px-2.5 py-1">
                          <CheckCircle2 size={11} className="text-brand-gold" /> {f}
                        </span>
                      ))}
                    </div>

                    <div className="mt-6 flex items-center justify-between border-t border-black/10 pt-5">
                      <div>
                        <div className="text-[0.62rem] tracking-[0.28em] uppercase text-brand-medium flex items-center gap-1.5"><Ruler size={11} /> {o.size}</div>
                        <div className="mt-1 font-serif text-brand-navy">{o.price}</div>
                      </div>
                      <button
                        onClick={() => scrollToForm()}
                        className="inline-flex items-center gap-2 text-[0.66rem] tracking-[0.28em] uppercase text-brand-navy hover:text-brand-gold transition-colors"
                        data-testid={`re-opp-cta-${i}`}
                      >
                        Danış &amp; Bilgi Al <ArrowRight size={14} />
                      </button>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* Map + lead */}
      <section id="re-lead-form" className="py-24 bg-[#0B1631] text-white">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <div className="text-[0.66rem] tracking-[0.3em] uppercase text-brand-gold">Konum</div>
            <h2 className="mt-4 font-serif text-3xl md:text-4xl">Yenişehir / Arnavutköy — Stratejik Bölge</h2>
            <p className="mt-4 text-white/80 max-w-2xl leading-relaxed">
              3. Havalimanı&apos;na 10 dk, Kanal İstanbul güzergâhına 15 dk. Kuzey Marmara Otoyolu, İstanbul-Kırklareli hattı ve TEM bağlantıları ile ulaşımı güçlü, imar planları netleşen bölgede yatırım fırsatları.
            </p>

            <div className="mt-8 aspect-video bg-white/5 border border-white/10 overflow-hidden">
              {embed ? (
                <iframe title="Yenişehir/Arnavutköy Konum" src={embed} className="w-full h-full" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
              ) : (
                <iframe title="Yenişehir/Arnavutköy Konum" src="https://www.google.com/maps?q=Yeni%C5%9Fehir%20Arnavutk%C3%B6y%20%C4%B0stanbul&output=embed" className="w-full h-full" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
              )}
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="bg-white text-brand-dark p-8 md:p-10">
              <div className="text-[0.66rem] tracking-[0.3em] uppercase text-brand-medium">Ücretsiz Yatırım Dosyası</div>
              <div className="font-serif text-3xl text-brand-navy mt-2">Danış &amp; Bilgi Al</div>
              <p className="text-sm text-brand-medium mt-3 mb-6">Danışmanımız fiyat listesi, imar planı ve tapu detaylarını size özel hazırlayarak arasın.</p>
              <BitrixFormEmbed forms={bitrixForms} formKey="buy" extraQuery={bitrixExtra} testId="re-main-bitrix" />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      {faq.length > 0 ? (
        <section className="py-24 bg-brand-navy text-white">
          <div className="mx-auto max-w-4xl px-6 md:px-10">
            <div className="text-[0.66rem] tracking-[0.3em] uppercase text-brand-gold">Sık Sorulan Sorular</div>
            <h2 className="mt-4 font-serif text-3xl md:text-4xl">Aklınıza takılanlar</h2>
            <div className="mt-10">
              <Faq items={faq} />
            </div>

            <div className="mt-12 flex flex-wrap gap-3">
              <button onClick={() => scrollToForm()} className="inline-flex items-center gap-2 bg-brand-gold hover:bg-brand-gold/90 text-brand-navy font-medium tracking-[0.22em] uppercase text-[0.72rem] px-8 py-4 transition-colors" data-testid="re-faq-cta">
                Danış &amp; Bilgi Al <ArrowRight size={16} />
              </button>
              {wa ? (
                <a href={`https://wa.me/${wa}?text=${encodeURIComponent(s.whatsapp_message || "Yenişehir arsa yatırımı hakkında bilgi almak istiyorum.")}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 border border-white/30 hover:border-brand-gold hover:text-brand-gold text-white tracking-[0.22em] uppercase text-[0.72rem] px-8 py-4 transition-colors" data-testid="re-faq-wa">
                  <MessageCircle size={14} /> WhatsApp
                </a>
              ) : null}
            </div>
          </div>
        </section>
      ) : null}

      {/* Site Footer */}
      <Footer settings={s} />

      {/* Sticky mobile CTA */}
      {phone || wa ? (
        <div className="md:hidden fixed bottom-4 left-4 right-4 z-40 flex gap-2">
          {phone ? (
            <a href={`tel:${phone}`} className="flex-1 inline-flex items-center justify-center gap-2 bg-brand-navy text-white py-3 text-[0.72rem] tracking-[0.24em] uppercase" data-testid="re-mobile-call">
              <Phone size={14} /> Ara
            </a>
          ) : null}
          {wa ? (
            <a href={`https://wa.me/${wa}`} target="_blank" rel="noreferrer" className="flex-1 inline-flex items-center justify-center gap-2 bg-brand-gold text-brand-navy font-medium py-3 text-[0.72rem] tracking-[0.24em] uppercase" data-testid="re-mobile-wa">
              <MessageCircle size={14} /> WhatsApp
            </a>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default RealEstateLandingPage;
